// src/components/upload/UploadProgressIndicator.tsx
import { useEffect, useState } from "react";
import { useUpload } from "@/contexts/UploadContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { X, Check, AlertTriangle, RefreshCcw, Upload } from "lucide-react";
import {
  getPendingUploadTasks,
  checkUploadStatus,
} from "@/lib/serviceWorkerClient";

const UploadProgressIndicator = () => {
  const { uploads, removeUpload } = useUpload();
  const [backgroundTasks, setBackgroundTasks] = useState<any[]>([]);
  const [isBackgroundTasksLoaded, setIsBackgroundTasksLoaded] = useState(false);

  // Load any background tasks from IndexedDB
  useEffect(() => {
    const loadBackgroundTasks = async () => {
      try {
        if ("serviceWorker" in navigator) {
          const tasks = await getPendingUploadTasks();
          setBackgroundTasks(tasks || []);
        }
      } catch (error) {
        console.error("Error loading background tasks:", error);
      } finally {
        setIsBackgroundTasksLoaded(true);
      }
    };

    loadBackgroundTasks();

    // Set up a periodic refresh
    const interval = setInterval(loadBackgroundTasks, 5000);
    return () => clearInterval(interval);
  }, []);

  // Listen for service worker messages to update the UI in real-time
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      const handleMessage = async (event: MessageEvent) => {
        const message = event.data;

        if (
          message.type === "UPLOAD_PROGRESS" ||
          message.type === "UPLOAD_COMPLETED" ||
          message.type === "UPLOAD_FAILED"
        ) {
          // Refresh our task list when we get updates
          const tasks = await getPendingUploadTasks();
          setBackgroundTasks(tasks || []);
        }
      };

      navigator.serviceWorker.addEventListener("message", handleMessage);
      return () => {
        navigator.serviceWorker.removeEventListener("message", handleMessage);
      };
    }
  }, []);

  // No uploads to show
  if (
    uploads.length === 0 &&
    (!isBackgroundTasksLoaded || backgroundTasks.length === 0)
  ) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-md w-full">
      {/* Show normal uploads from UploadContext */}
      {uploads.map((upload) => (
        <div
          key={upload.id}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 flex flex-col gap-2 border border-gray-200 dark:border-gray-700"
        >
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              {upload.status === "uploading" ||
              upload.status === "processing" ? (
                <Upload className="h-4 w-4 text-blue-500 animate-pulse" />
              ) : upload.status === "success" ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-red-500" />
              )}
              <span className="font-medium text-sm">
                {upload.type === "album" ? "Album" : "Photo"}: {upload.title}
              </span>
            </div>

            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => removeUpload(upload.id)}
              disabled={
                upload.status === "uploading" || upload.status === "processing"
              }
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </Button>
          </div>

          <div className="w-full">
            <Progress
              value={upload.progress}
              className="h-2"
              {...(upload.status === "error" && {
                className: "h-2 bg-red-200 dark:bg-red-900",
              })}
            />
          </div>

          <div className="flex justify-between text-xs text-gray-500">
            <span>
              {upload.status === "success"
                ? "Upload complete"
                : upload.status === "error"
                ? "Upload failed"
                : `${upload.completedFiles} of ${upload.totalFiles} files (${upload.progress}%)`}
            </span>

            {upload.status === "uploading" && upload.estimatedTimeRemaining && (
              <span>
                {upload.estimatedTimeRemaining < 60
                  ? `${upload.estimatedTimeRemaining}s remaining`
                  : `${Math.ceil(
                      upload.estimatedTimeRemaining / 60
                    )}m remaining`}
              </span>
            )}
          </div>

          {upload.status === "error" && upload.error && (
            <div className="text-xs text-red-500 mt-1">{upload.error}</div>
          )}
        </div>
      ))}

      {/* Show background tasks from the service worker */}
      {backgroundTasks.map((task) => (
        <div
          key={task.id}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 flex flex-col gap-2 border border-gray-200 dark:border-gray-700"
        >
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              {task.status === "uploading" || task.status === "pending" ? (
                <RefreshCcw className="h-4 w-4 text-blue-500 animate-spin" />
              ) : task.status === "completed" ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-red-500" />
              )}
              <span className="font-medium text-sm">
                Background Upload: {task.title}
              </span>
            </div>
          </div>

          <div className="w-full">
            <Progress
              value={task.progress || 0}
              className="h-2"
              {...(task.status === "failed" && {
                className: "h-2 bg-red-200 dark:bg-red-900",
              })}
            />
          </div>

          <div className="flex justify-between text-xs text-gray-500">
            <span>
              {task.status === "completed"
                ? "Upload complete"
                : task.status === "failed"
                ? "Upload failed"
                : task.status === "pending"
                ? "Starting upload..."
                : `Uploading in background (${task.progress || 0}%)`}
            </span>

            <span className="text-xs italic font-light">
              Will continue even if you close the page
            </span>
          </div>

          {task.status === "failed" && task.error && (
            <div className="text-xs text-red-500 mt-1">{task.error}</div>
          )}
        </div>
      ))}
    </div>
  );
};

export default UploadProgressIndicator;
